<template>
  <edit-course-header :course="course" :can-view-lti-link="!!can?.viewLtiLink" />
  <form
    @submit.prevent="form.put(route('courses.assignments.index.update', {course: course.id}))"
    class="flex-1 flex flex-col"
  >
    <s-page-block size="md" class="flex-1">
      <s-form-list
        v-model="form.items"
        data-list-type="main-list"
        :group="{
          name: 'assignments',
          put: true,
          pull: true,
        }"
        sortable
        :item-key="(item: OrderItem) => item.id || item.key"
        :item-visible="(item: OrderItem) => !item.delete"
        :itemType="(item: OrderItem) => item.type"
      >
        <template #item="{item, index}">
          <fieldset class="flex" role="group">
            <legend class="sr-only">
              {{ t('legend', {name: form.items[index].name, index: index + 1}) }}
            </legend>

            <div class="w-full flex flex-col">
              <div class="flex-1 p-2.5 flex items-center gap-2">
                <s-input
                  v-model="form.items[index].name"
                  :data-form-index="index"
                  required
                  class="w-full"
                  :aria-label="t('aria.labels.name', {index: index + 1})"
                  :placeholder="item.type === 'module' ? t('module_name') : t('assignment_name')"
                />
                <s-btn
                  v-if="item.type === 'module'"
                  :icon="item.collapsed ? 'chevron-down' : 'chevron-up'"
                  color="white"
                  @click.prevent="item.collapsed = !item.collapsed"
                >
                  {{
                    item.collapsed
                      ? t('expand_assignments', item.assignments.length)
                      : t('collapse')
                  }}
                </s-btn>
              </div>
              <template v-if="item.type === 'module'">
                <div v-if="!item.collapsed" class="w-full px-3 pb-3">
                  <s-form-list
                    v-model="
                      // @ts-ignore
                      form.items[index].assignments
                    "
                    data-list-type="module"
                    :group="{
                      name: 'assignments',
                      put: (to: any, from: any, dragEl: any) => {
                        const type = (dragEl as HTMLElement).getAttribute('data-list-type');
                        return type === 'assignment';
                      },
                      pull: true,
                    }"
                    handle="module-handle"
                    :itemType="(item: OrderItem) => item.type"
                    sortable
                    :item-key="(item: OrderItem) => item.id ?? item.key"
                    :item-visible="(item: OrderItem) => !item.delete"
                    :placeholder="t('no_assignments')"
                  >
                    <template #item="{item: moduleItem, index: moduleIndex}">
                      <fieldset
                        class="flex"
                        role="group"
                        v-if="
                          // @ts-ignore
                          form.items[index].assignments[moduleIndex]
                        "
                      >
                        <div class="flex-1 p-2.5">
                          <s-input
                            v-model="
                              // @ts-ignore
                              form.items[index].assignments[moduleIndex].name
                            "
                            :data-form-index="moduleIndex"
                            required
                            class="w-full"
                            :aria-label="t('aria.labels.name', {index: index + 1})"
                            :placeholder="t('assignment_name')"
                          />
                        </div>
                        <s-tooltip
                          :text="t('cant_remove_assignment')"
                          wrapper-class="flex"
                          :disabled="canDeleteItem(moduleItem)"
                        >
                          <button
                            type="button"
                            class="text-red-300 hover:text-red-500 flex-none flex items-center justify-center py-2.5 pl-2.5 pr-3 opacity-70 hover:opacity-100 transition-all ease-out duration-150 bg-white hover:bg-gray-200/20 disabled:text-gray-300 disabled:bg-transparent disabled:cursor-not-allowed border-l border-gray-200"
                            :disabled="!canDeleteItem(moduleItem)"
                            @click.prevent="
                              handleRemoveAssignmentFromModule(moduleItem, index, moduleIndex)
                            "
                          >
                            <s-icon name="trash-can-outline" size="20" />
                          </button>
                        </s-tooltip>
                      </fieldset>
                    </template>
                  </s-form-list>
                  <div class="flex justify-end items-center mt-1.5">
                    <s-btn
                      @click.prevent="handleAddAssignmentToModule(index)"
                      icon="plus"
                      color="blue-light"
                      size="sm"
                    >
                      {{ t('add_assignment') }}
                    </s-btn>
                  </div>
                </div>
              </template>
            </div>

            <s-tooltip
              :text="t('cant_remove_assignment')"
              wrapper-class="flex"
              :disabled="!canDeleteItem(item)"
            >
              <button
                type="button"
                @click.prevent="handleRemoveRootItem(item, index)"
                class="text-red-300 hover:text-red-500 flex-none flex items-center justify-center py-2.5 pl-2.5 pr-3 opacity-70 hover:opacity-100 transition-all ease-out duration-150 bg-white hover:bg-gray-200/20 disabled:text-gray-300 disabled:bg-transparent disabled:cursor-not-allowed border-l border-gray-200"
                :disabled="!canDeleteItem(item)"
                :aria-label="
                  t('aria.actions.deleteAssignment', {
                    name: form.items[index].name,
                    index: index + 1,
                  })
                "
              >
                <s-icon name="trash-can-outline" size="20" />
              </button>
            </s-tooltip>
          </fieldset>
        </template>
      </s-form-list>
      <div class="mt-3 flex justify-end">
        <s-btn
          type="button"
          @click.prevent="handleAddModule()"
          icon="plus"
          color="blue-light"
          class="!rounded-r-none"
        >
          {{ t('add_module') }}
        </s-btn>
        <s-btn
          type="button"
          @click.prevent="handleAddAssignment()"
          icon="plus"
          color="blue-light"
          class="!rounded-l-none !border-l-0"
        >
          {{ t('add_assignment') }}
        </s-btn>
      </div>
    </s-page-block>
    <s-form-footer
      :cancelRoute="route('courses.assignments.index', {course: course.id})"
      :formProcessing="form.processing"
      :formIsDirty="form.isDirty"
      size="md"
    />
  </form>
</template>
<script setup lang="ts">
import EditCourseHeader from '../../layouts/EditCourseHeader.vue';
import SFormFooter from '../../design-system/SFormFooter.vue';
import SFormList from '../../design-system/SFormList.vue';
import SInput from '../../design-system/SInput.vue';
import {Course} from '../../types/entities/course';
import SPageBlock from '../../design-system/SPageBlock.vue';
import {useI18n} from 'vue-i18n';
import STooltip from '../../design-system/STooltip.vue';
import SIcon from '../../design-system/SIcon.vue';
import {useForm} from '@inertiajs/vue3';
import {route} from 'ziggy-js';
import {nextTick, ref} from 'vue';
import {useKeyboardShortcuts} from '../../composables/useKeyboardShortcut';
import SBtn from '../../design-system/SBtn.vue';
import SBadgeGroup from '../../design-system/SBadgeGroup.vue';
import {v4 as uuidv4} from 'uuid';

type OModule = {
  type: 'module';
  id: number | null;
  name: string;
  delete?: boolean;
  assignments: OAssignment[];
  collapsed?: boolean;
  key?: string;
};

type OAssignment = {
  type: 'assignment';
  id: number | null;
  name: string;
  canDelete?: boolean;
  delete?: boolean;
  key?: string;
};

type OrderItem = OModule | OAssignment;

const {items, course} = defineProps<{
  course: Course;
  items: OrderItem[];
  can?: {
    viewLtiLink?: boolean;
  };
}>();

const form = useForm({
  items: items,
  toBeDeleted: [] as OrderItem[],
});

const {t} = useI18n({
  inheritLocale: true,
  useScope: 'local',
});

const newAssignmentName = ref('');

const handleRemoveRootItem = (item: OrderItem, index: number) => {
  if (canDeleteItem(item)) {
    if (item.id) {
      form.toBeDeleted.push(item);
    }
    form.items.splice(index, 1);
  }
};

const handleRemoveAssignmentFromModule = (
  moduleItem: OAssignment,
  index: number,
  moduleIndex: number
) => {
  if (moduleItem.canDelete) {
    if (moduleItem.id) {
      form.toBeDeleted.push(moduleItem);
    }
    // @ts-ignore
    form.items[index].assignments.splice(moduleIndex, 1);
  }
};

function canDeleteItem(item: OrderItem): boolean {
  if (item.type === 'assignment') {
    return !!item.canDelete;
  } else if (item.type === 'module') {
    return !item.assignments.length || item.assignments.every(canDeleteItem);
  }
  return false;
}

const keyboardShortcuts = useKeyboardShortcuts();

keyboardShortcuts.register({
  key: 'N',
  label: t('add_assignment'),
  onPress(e) {
    const target = e.target as HTMLElement;
    const currentIndex = ~~(target.getAttribute('data-form-index') ?? form.items.length - 1);
    const nextIndex = (currentIndex ?? form.items.length - 1) + 1;

    form.items = [
      ...form.items.slice(0, nextIndex),
      {
        type: 'assignment',
        id: null,
        name: newAssignmentName.value,
        canDelete: true,
        key: uuidv4(),
      },
      ...form.items.slice(nextIndex),
    ];

    newAssignmentName.value = '';

    // Focus the new assignment input by the data-form-index attribute
    nextTick(() => {
      const newInput = document.querySelector(
        `[data-form-index="${nextIndex}"]`
      ) as HTMLInputElement | null;

      if (newInput) {
        newInput.focus();
      }
    });
  },
  metaKey: true,
  shiftKey: true,
  ctrlKey: true,
});

const handleAddAssignment = () => {
  form.items = [
    ...form.items,
    {
      type: 'assignment',
      id: null,
      name: '',
      canDelete: true,
      key: uuidv4(),
    },
  ];
};

const handleAddModule = () => {
  form.items = [
    ...form.items,
    {
      type: 'module',
      id: null,
      name: '',
      assignments: [],
      key: uuidv4(),
    },
  ];
};

const handleAddAssignmentToModule = (index: number) => {
  if (form.items[index]?.type === 'module') {
    // @ts-ignore
    form.items[index].assignments = [
      // @ts-ignore
      ...form.items[index].assignments,
      {
        type: 'assignment',
        id: null,
        name: '',
        canDelete: true,
        key: uuidv4(),
      },
    ];
  }
};
</script>
<i18n>
{
  "en": {
    "aria.actions.deleteAssignment": "Delete assignment #{index}, {name}",
    "aria.labels.name": "Name for assignment #{index}",
    "legend": "Assignment #{index}, {name}",
    "add_assignment": "Add Assignment",
    "cant_remove_assignment": "Cannot delete assignments with existing responses.",
    "add_module": "Add Module",
    "collapse": "Collapse",
    "expand_assignments": "Expand {count} Assignment | Expand {count} Assignments",
    "no_assignments": "No assignments",
    "module_name": "Module Name",
    "assignment_name": "Assignment Name"
  },
  "fr": {
    "aria.actions.deleteAssignment": "Supprimer le devoir, {name}",
    "aria.labels.name": "Nom du devoir #{index}",
    "legend": "Devoir #{index}, {name}",
    "add_assignment": "Ajouter un devoir",
    "cant_remove_assignment": "Impossible de supprimer les devoirs avec des réponses existantes.",
    "add_module": "Ajouter un module",
    "collapse": "Réduire",
    "expand_assignments": "Développer {count} devoir | Développer {count} devoirs",
    "no_assignments": "Aucun devoir",
    "module_name": "Nom du module",
    "assignment_name": "Nom du devoir"
  }
}
</i18n>
